/**
 * The money side of the banking page: what an account holds, whether that agrees with the
 * statement, which rows nothing explains yet, the other half of a transfer, and the cash book.
 *
 * Pure functions over rows already loaded; the ledger itself is written by the server
 * (lib/ledger/money.ts through pb_hooks/ledger.pb.js).
 */
import type { BankAccount, Transaction } from "@/lib/api/banking";

const day = (value: string | undefined) => (value ?? "").slice(0, 10);
const cents = (euros: number) => Math.round(euros * 100);

/** Rows that move the account's balance: from the opening day on (earlier ones are in it). */
function counted(account: BankAccount, transactions: Transaction[], upTo?: string) {
  const from = day(account.openingDate);
  return transactions.filter(
    (row) =>
      row.account === account.id &&
      (!from || day(row.bookingDate) >= from) &&
      (!upTo || day(row.bookingDate) <= upTo),
  );
}

/**
 * Transfers booked on another account that arrive here and have no half of their own on this
 * account - money taken from the bank into the till, which has no statement.
 */
function arriving(account: BankAccount, transactions: Transaction[], upTo?: string) {
  const paired = new Set(
    transactions.filter((row) => row.transferOf).flatMap((row) => [row.id, row.transferOf]),
  );
  const from = day(account.openingDate);
  return transactions.filter(
    (row) =>
      row.account !== account.id &&
      row.status === "ignored" &&
      row.bookingType === "transfer" &&
      row.transferAccount === account.id &&
      !paired.has(row.id) &&
      (!from || day(row.bookingDate) >= from) &&
      (!upTo || day(row.bookingDate) <= upTo),
  );
}

/**
 * Opening balance plus every imported or entered row up to `upTo` (inclusive), plus transfers in
 * that only the other account recorded, in euros.
 */
export function accountBalance(
  account: BankAccount,
  transactions: Transaction[],
  upTo?: string,
): number {
  const own = counted(account, transactions, upTo).reduce(
    (sum, row) => sum + cents(row.amount),
    cents(account.openingBalance ?? 0),
  );
  const transfers = arriving(account, transactions, upTo).reduce(
    (sum, row) => sum - cents(row.amount),
    0,
  );
  return (own + transfers) / 100;
}

/**
 * Statement minus what was imported, on the statement's day: 0 means nothing is missing, anything
 * else a row that was not imported (or one too many). Null while no statement balance is set.
 */
export function statementDifference(
  account: BankAccount,
  transactions: Transaction[],
): number | null {
  const date = day(account.statementDate);
  if (!date || account.statementBalance === undefined || account.statementBalance === null) {
    return null;
  }
  return (
    (cents(account.statementBalance) - cents(accountBalance(account, transactions, date))) / 100
  );
}

/** Whether the ledger knows what a row is: a receipt, or a booking without one. */
export function isExplained(row: Pick<Transaction, "status" | "bookingType" | "document">) {
  if (row.status === "matched") return Boolean(row.document);
  return row.status === "ignored" && Boolean(row.bookingType);
}

/** Rows of the account, from its opening day, that nothing explains yet - money in included. */
export function unexplained(account: BankAccount, transactions: Transaction[]): Transaction[] {
  return counted(account, transactions).filter(
    (row) => !isExplained(row) && !row.possibleDuplicateOf,
  );
}

/**
 * The other half of a transfer: on the other account, the same amount the other way, within five
 * days, and not explained by anything else. The closest date wins.
 */
export function transferMirror(
  row: Transaction,
  otherAccount: string,
  transactions: Transaction[],
): Transaction | null {
  const days = (a: string, b: string) =>
    Math.abs(Date.parse(day(a)) - Date.parse(day(b))) / 86_400_000;
  const candidates = transactions.filter(
    (other) =>
      other.account === otherAccount &&
      other.id !== row.id &&
      cents(other.amount) === -cents(row.amount) &&
      days(other.bookingDate, row.bookingDate) <= 5 &&
      !other.transferOf &&
      (other.status === "unmatched" ||
        (other.status === "ignored" && (!other.bookingType || other.bookingType === "transfer"))),
  );
  candidates.sort(
    (a, b) => days(a.bookingDate, row.bookingDate) - days(b.bookingDate, row.bookingDate),
  );
  return candidates[0] ?? null;
}

export type LedgerLineRow = {
  id: string;
  debit: number;
  credit: number;
  entry: {
    id: string;
    seq: number;
    date: string;
    source: string;
    reverses: string;
    docNumber: string;
    partnerName: string;
  };
};

export type CashBookRow = {
  id: string;
  date: string;
  docNumber: string;
  text: string;
  /** Euros; positive is money in. */
  amount: number;
  balance: number;
};

/**
 * The cash book: every movement on the till's Sachkonto in date order, with the running balance.
 * Entries the ledger later reversed are left out together with their reversals. A till cannot
 * hold less than nothing, so `negativeOn` names the first day it would.
 */
export function cashBook(lines: LedgerLineRow[]): { rows: CashBookRow[]; negativeOn: string } {
  const reversed = new Set(lines.map((line) => line.entry.reverses).filter(Boolean));
  const kept = lines
    .filter((line) => !reversed.has(line.entry.id) && line.entry.source !== "reversal")
    .sort((a, b) => a.entry.date.localeCompare(b.entry.date) || a.entry.seq - b.entry.seq);
  let balance = 0;
  let negativeOn = "";
  const rows = kept.map((line) => {
    balance += line.debit - line.credit;
    if (balance < 0 && !negativeOn) negativeOn = line.entry.date;
    return {
      id: line.id,
      date: line.entry.date,
      docNumber: line.entry.docNumber,
      text: line.entry.partnerName,
      amount: (line.debit - line.credit) / 100,
      balance: balance / 100,
    };
  });
  return { rows, negativeOn };
}
